import React, { useState, useEffect } from 'react';
import {
  Alert,
  Modal,
  Image,
  Empty,
  Spin,
  Popconfirm,
  Button,
  Upload,
  message,
} from 'antd';
import {
  CameraOutlined,
  DeleteOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useTranslation } from 'react-i18next';
import type { PlotPhoto } from '../types/audit';
import { photoService } from '../services/api/photoService';
import dayjs from 'dayjs';

interface PhotoGalleryModalProps {
  visible: boolean;
  onClose: () => void;
  entityId: string;
  entityName: string;
  entityType: 'plot' | 'controlPoint' | 'adjacentCrop';
  onPhotoDeleted?: () => void;
  /** Enable photo uploads from this modal. When provided, the caller
   *  must give the audit id, plus — for review-linked entities (CP /
   *  adjacent crop) — a resolver that returns the *review id* the
   *  photo should attach to. The resolver is called lazily so the
   *  caller can auto-create a placeholder review the first time the
   *  auditor uploads a photo. */
  upload?: {
    auditId: number;
    /** Returns the review id that the photo should be linked to.
     *  For `entityType: 'plot'`, return the audit_plot id (same as
     *  entityId). For control points / adjacent crops, return the
     *  ControlPointReview / AdjacentCropReview id for this audit. */
    getTargetId: () => Promise<string>;
  };
  /** Fires after at least one successful upload so the parent can
   *  refresh counts / summaries. */
  onPhotoUploaded?: () => void;
}

/**
 * PhotoGalleryModal Component
 * Generic photo gallery modal that works for both audit plots and control points
 */
export const PhotoGalleryModal: React.FC<PhotoGalleryModalProps> = ({
  visible,
  onClose,
  entityId,
  entityName,
  entityType,
  onPhotoDeleted,
  upload,
  onPhotoUploaded,
}) => {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState<PlotPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleUpload = async () => {
    if (!upload || pendingFiles.length === 0) return;
    setUploading(true);
    setUploadError(null);
    try {
      // Resolve / auto-create the target FK once for the whole batch
      // — for adjacent crops & control points this triggers a
      // createReview when none exists yet for this audit.
      const targetId = await upload.getTargetId();
      const targetType: "audit_plot" | "control_point_review" | "adjacent_crop_review" =
        entityType === "plot"
          ? "audit_plot"
          : entityType === "controlPoint"
            ? "control_point_review"
            : "adjacent_crop_review";

      const successes: PlotPhoto[] = [];
      const failures: string[] = [];
      for (const item of pendingFiles) {
        const file = (item.originFileObj as File | undefined) ?? null;
        if (!file) continue;
        try {
          const created = await photoService.uploadPhotoForEntity(
            upload.auditId,
            { type: targetType, id: targetId },
            file,
          );
          successes.push(created);
        } catch (e: any) {
          failures.push(
            `${file.name} — ${e?.message || "Error al subir"}`,
          );
        }
      }

      if (successes.length > 0) {
        // Prepend new ones so they're visible at the top.
        setPhotos((prev) => [...successes, ...prev]);
        message.success(
          `${successes.length} fotograf${successes.length === 1 ? "ía" : "ías"} subida${successes.length === 1 ? "" : "s"}`,
        );
        onPhotoUploaded?.();
      }
      if (failures.length > 0) {
        setUploadError(failures.join("\n"));
      }
      // Clear the pending list (or only the successes) — easier to
      // clear all so the user knows what's left to retry.
      setPendingFiles([]);
    } catch (e: any) {
      setUploadError(e?.message || "No se pudo preparar la subida");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (visible && entityId) {
      loadPhotos();
    }
  }, [visible, entityId, entityType]);

  const loadPhotos = async () => {
    try {
      setLoading(true);
      let data: PlotPhoto[];
      
      if (entityType === 'plot') {
        data = await photoService.getPhotosByAuditPlot(entityId);
      } else if (entityType === 'controlPoint') {
        data = await photoService.getPhotosByControlPoint(entityId);
      } else if (entityType === 'adjacentCrop') {
        data = await photoService.getPhotosByAdjacentCrop(entityId);
      } else {
        data = [];
      }
      
      // Ensure data is an array
      if (!Array.isArray(data)) {
        console.error('Photos data is not an array:', data);
        setPhotos([]);
      } else {
        setPhotos(data);
      }
    } catch (error: any) {
      console.error('Error loading photos:', error);
      message.error(
        error.message ||
          t('photo.load_error') ||
          'Error al cargar las fotografías'
      );
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    try {
      await photoService.deletePhoto(photoId);
      message.success(
        t('photo.delete_success') || 'Fotografía eliminada correctamente'
      );

      // Remove from local state
      setPhotos(photos.filter((p) => p.id !== photoId));

      // Notify parent
      if (onPhotoDeleted) {
        onPhotoDeleted();
      }
    } catch (error: any) {
      message.error(
        error.message ||
          t('photo.delete_error') ||
          'Error al eliminar la fotografía'
      );
    }
  };

  const getTitle = () => {
    const prefix = t('photo.gallery_title') || 'Fotografías de';
    return `${prefix} ${entityName}`;
  };

  const getEmptyDescription = () => {
    if (entityType === 'plot') {
      return t('photo.no_photos') || 'No hay fotografías para esta parcela';
    } else if (entityType === 'controlPoint') {
      return t('photo.no_photos_control_point') || 'No hay fotografías para este punto de control';
    } else if (entityType === 'adjacentCrop') {
      return t('photo.no_photos_adjacent_crop') || 'No hay fotografías para este cultivo adyacente';
    }
    return t('photo.no_photos') || 'No hay fotografías';
  };

  return (
    <Modal
      title={getTitle()}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
    >
      {upload && (
        <div style={{ marginBottom: 16 }}>
          <Upload.Dragger
            multiple
            accept="image/jpeg,image/png,image/jpg"
            beforeUpload={() => false}
            fileList={pendingFiles}
            onChange={({ fileList }) => setPendingFiles(fileList)}
            onRemove={(file) =>
              setPendingFiles((prev) => prev.filter((f) => f.uid !== file.uid))
            }
            disabled={uploading}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {t(
                'photo.upload_hint',
                'Arrastra fotos aquí o haz clic para seleccionarlas',
              )}
            </p>
            <p className="ant-upload-hint" style={{ fontSize: 12 }}>
              {t(
                'photo.upload_subhint',
                'JPG / PNG · hasta 10 MB por archivo',
              )}
            </p>
          </Upload.Dragger>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 12,
              gap: 8,
            }}
          >
            <Button
              type="primary"
              icon={<CameraOutlined />}
              loading={uploading}
              disabled={pendingFiles.length === 0}
              onClick={handleUpload}
            >
              {pendingFiles.length > 1
                ? t('photo.upload_n', 'Subir {{n}} fotos', {
                    n: pendingFiles.length,
                  })
                : t('photo.upload', 'Subir foto')}
            </Button>
          </div>
          {uploadError && (
            <Alert
              type="error"
              showIcon
              closable
              onClose={() => setUploadError(null)}
              style={{ marginTop: 12 }}
              message={t(
                'photo.upload_partial_error',
                'Algunas fotos no se subieron',
              )}
              description={
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                  {uploadError}
                </pre>
              }
            />
          )}
        </div>
      )}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : photos.length === 0 ? (
        <Empty description={getEmptyDescription()} />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '16px',
          }}
        >
          {photos.map((photo) => {
            // Construct full URL if image_url is relative
            let imageUrl = photo.image_url;
            if (imageUrl && !imageUrl.startsWith('http')) {
              // In development, use backend URL; in production, use same origin
              const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
              if (apiBaseUrl.startsWith('http')) {
                // Full URL like http://localhost:8010/api
                imageUrl = apiBaseUrl.replace('/api', '') + imageUrl;
              } else {
                // Relative URL like /api
                imageUrl = window.location.origin + imageUrl;
              }
            }
            
            return (
            <div
              key={photo.id}
              style={{
                position: 'relative',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <Image
                src={imageUrl}
                alt={photo.description || entityName}
                style={{ width: '100%', height: '200px', objectFit: 'cover' }}
              />
              <div style={{ padding: '8px' }}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {dayjs(photo.created).format('DD/MM/YYYY HH:mm')}
                </div>
                {photo.description && (
                  <div
                    style={{
                      fontSize: '12px',
                      marginTop: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {photo.description}
                  </div>
                )}
                <Popconfirm
                  title={
                    t('photo.delete_confirm') ||
                    '¿Está seguro de eliminar esta fotografía?'
                  }
                  onConfirm={() => handleDeletePhoto(photo.id)}
                  okText={t('common.yes') || 'Sí'}
                  cancelText={t('common.no') || 'No'}
                >
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    style={{ marginTop: '8px', width: '100%' }}
                  >
                    {t('common.delete') || 'Eliminar'}
                  </Button>
                </Popconfirm>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};
